import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { EAIChatPageId } from 'types/ai';
import { EPPAStep, IPPAPersonalIdentification } from 'types/ppaOnboarding';
import usePPAOnboardingReviewInfo from 'hooks/usePPAOnboardingReviewInfo';
import { getStep, savePPAPersonalIdentification, saveStep } from 'api/ppaOnboarding';
import { EInvitedProviderStep, INVITED_STEP_ROUTE, isInvitedProviderStep } from '../steps';
import ListOnKlarityHealth3 from 'pages/PPAOnboardingSteps/components/Steps/ListOnKlarityHealth/Step3';
import ListOnKlarityHealth4 from 'pages/PPAOnboardingSteps/components/Steps/ListOnKlarityHealth/Step4';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { EInvitedOnboardingStep } from '../types';

type SubStep = EPPAStep.STEP_11 | EPPAStep.STEP_12;

const SUB_STEP_ORDER: SubStep[] = [EPPAStep.STEP_11, EPPAStep.STEP_12];

const FORM_IDS: Record<SubStep, string> = {
    [EPPAStep.STEP_11]: 'invited-personal-id',
    [EPPAStep.STEP_12]: 'invited-medical-license',
};

const AI_CHAT_BY_SUB_STEP: Record<SubStep, EAIChatPageId> = {
    [EPPAStep.STEP_11]: EAIChatPageId.INVITED_PROVIDER_MARKETING_PERSONAL_ID,
    [EPPAStep.STEP_12]: EAIChatPageId.INVITED_PROVIDER_MARKETING_MEDICAL_LICENSE,
};

const MarketingProfilePage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const cameFromBack = (location.state as { fromNext?: boolean } | null)?.fromNext === false;
    const [reviewData, , refetchReviewData] = usePPAOnboardingReviewInfo();
    const [subStep, setSubStep] = useState<SubStep>(cameFromBack ? EPPAStep.STEP_12 : EPPAStep.STEP_11);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [nextDisabled, setNextDisabled] = useState(false);

    useEffect(() => {
        setNextDisabled(false);
    }, [subStep]);

    useEffect(() => {
        if (cameFromBack) return undefined;
        let cancelled = false;
        (async () => {
            const res = await getStep();
            const saved = res?.data?.data?.currentStep;
            if (cancelled || !isInvitedProviderStep(saved)) return;
            if (saved === EInvitedProviderStep.MARKETING_PERSONAL_ID) return;
            if (saved === EInvitedProviderStep.MARKETING_MEDICAL_LICENSE) {
                setSubStep(EPPAStep.STEP_12);
                return;
            }
            const target = INVITED_STEP_ROUTE[saved];
            if (target && target !== PATH.INVITED_PROVIDER_ONBOARDING_MARKETING_PROFILE) {
                navigate(target, { replace: true });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const goToNextSubStep = useCallback(async () => {
        const idx = SUB_STEP_ORDER.indexOf(subStep);
        refetchReviewData?.();
        if (idx < SUB_STEP_ORDER.length - 1) {
            await saveStep(EInvitedProviderStep.MARKETING_MEDICAL_LICENSE);
            setSubStep(SUB_STEP_ORDER[idx + 1]);
        } else {
            await saveStep(EInvitedProviderStep.AVAILABILITY_WORKING_HOURS);
            navigate(PATH.INVITED_PROVIDER_ONBOARDING_AVAILABILITY);
        }
    }, [subStep, navigate, refetchReviewData]);

    const handleSavePersonalId = useCallback(
        async (data: IPPAPersonalIdentification) => {
            try {
                setSubmitLoading(true);
                const res = await savePPAPersonalIdentification(data);
                if (res && !res.error) {
                    goToNextSubStep();
                } else {
                    message.error(res?.error);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setSubmitLoading(false);
            }
        },
        [goToNextSubStep],
    );

    const handleBack = useCallback(() => {
        const idx = SUB_STEP_ORDER.indexOf(subStep);
        if (idx > 0) {
            setSubStep(SUB_STEP_ORDER[idx - 1]);
        } else {
            navigate(PATH.INVITED_PROVIDER_ONBOARDING_SERVICES, { state: { fromNext: false } });
        }
    }, [subStep, navigate]);

    const formId = FORM_IDS[subStep];

    const contentRender = useMemo(() => {
        if (subStep === EPPAStep.STEP_11) {
            return (
                <ListOnKlarityHealth3
                    formId={formId}
                    onNext={handleSavePersonalId}
                    onSaveStep={goToNextSubStep}
                    setNextButtonDisabled={setNextDisabled}
                    initValue={reviewData.identification}
                />
            );
        }
        return (
            <ListOnKlarityHealth4
                formId={formId}
                onSaveStep={goToNextSubStep}
                setNextButtonDisabled={setNextDisabled}
                initValue={reviewData.licenses}
            />
        );
    }, [subStep, formId, handleSavePersonalId, goToNextSubStep, reviewData]);

    return (
        <Layout
            step={EInvitedOnboardingStep.MARKETING_PROFILE}
            aiChatPageId={AI_CHAT_BY_SUB_STEP[subStep]}
            footer={
                <Footer
                    showBack
                    onBack={handleBack}
                    nextFormId={formId}
                    nextLoading={submitLoading}
                    nextDisabled={nextDisabled}
                />
            }
        >
            {contentRender}
        </Layout>
    );
};

export default MarketingProfilePage;
